// ctsk-073 — Watching panel (dedicated /watching route).
//
// The "What's on my plate" view. Two server-side filtered lists side
// by side (Documents you're watching · Work items you're watching),
// each a click away from the source record. The Documents tab uses
// `?watchedBy=me` (ctsk-051) and the Work items tab uses
// `?watchedBy=me` from #112.
//
// FR 2026-05-29 — Cross-org Watching. Both queries opt into the new
// `?scope=all-my-orgs` mode so the panel fans out across every org
// the caller is a member of. Each row is stamped with `.org` and the
// page renders an Org filter chip strip + an "Org" column in both
// tabs. URL mirrors the active org filter as `?orgId=…` so the
// filter survives reload + share.
//
// Layout: tabs at the top (Documents · Work items), org chip strip
// underneath (visible whenever the caller has ≥2 orgs in the
// combined data), one list per tab, count badge on each tab. Each
// row is compact: title + a secondary line showing key/status,
// clicking opens the detail. A star-button per row unwatches in
// place (mutates via the existing /:id/watch DELETE).
//
// v1 explicitly NOT shipping: notifications, mentions, due-soon
// rollup. Those land in Roadmap 6 (Notifications) and ctsk-067.
// Keep the page lean so the next iteration can add features without
// a redesign.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DocumentListItem, Project, WorkItem } from '@captain-tasko/shared';
import { apiFetch, apiList } from '../lib/api.js';
import { MimeIcon } from '../components/MimeIcon.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { EmptyState } from '../components/EmptyState.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { toast } from '../components/Toast.js';
import { formatWorkItemKey, iconLabelForMime } from '../lib/format.js';
import { useSession } from '../hooks/useSession.js';
import { DocumentCardWall } from '../components/documents/DocumentCardWall.js';
import { CardViewBar } from '../components/documents/CardViewBar.js';
import { useCardView, type CardViewState } from '../components/documents/useCardView.js';
import { useDocCardActions } from '../components/documents/useDocCardActions.js';
import { rememberOpened } from '../components/documents/viewPrefs.js';

type Tab = 'documents' | 'workitems';

/** Read the orgId filter from the URL on first mount. */
function readOrgIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  const v = sp.get('orgId');
  return v && v.length > 0 ? v : null;
}

/** Mirror the orgId filter into the URL without dropping any other params. */
function writeOrgIdToUrl(orgId: string | null) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (orgId) url.searchParams.set('orgId', orgId);
  else url.searchParams.delete('orgId');
  window.history.replaceState(null, '', url.toString());
}

export function Watching({ onNavigate }: { onNavigate: (p: string) => void }) {
  const { session } = useSession();
  const activeOrgId = session?.activeOrg?.id ?? null;
  const [tab, setTab] = useState<Tab>('documents');
  const cardView = useCardView(session?.user?.id ?? null, activeOrgId);
  const [orgFilter, setOrgFilter] = useState<string | null>(() => readOrgIdFromUrl());

  // Page sizes are intentionally bigger here than on the source
  // pages — the whole point of /watching is to see your queue in one
  // shot. 200 lets a long-time user see their full watch set without
  // pagination chrome; if it ever gets bigger than that we'll add
  // an explicit pager.
  const docsQ = useQuery({
    queryKey: ['documents', { watchedBy: 'me', scope: 'all-my-orgs' }],
    queryFn: () =>
      apiList<DocumentListItem>('/documents', {
        query: { watchedBy: 'me', scope: 'all-my-orgs', limit: 200 },
      }),
    staleTime: 10_000,
  });
  const itemsQ = useQuery({
    queryKey: ['workitems', { watchedBy: 'me', scope: 'all-my-orgs' }],
    queryFn: () =>
      apiList<WorkItem>('/workitems', {
        query: { watchedBy: 'me', scope: 'all-my-orgs', limit: 200 },
      }),
    staleTime: 10_000,
  });
  // 0.1.51 — projects for per-key color lookup. Scoped to active-org
  // (not cross-org) since projects are per-org and Watching's
  // cross-org fanout doesn't include a project-list endpoint. Rows
  // whose projectId isn't in this list fall back to --text-muted;
  // acceptable since cross-org rows are visually separated by the
  // OrgChip anyway.
  const projectsQ = useQuery({
    queryKey: ['projects'],
    queryFn: () => apiList<Project>('/projects'),
    staleTime: 60_000,
  });

  // Build the combined org distribution from both lists. Each row
  // carries its own .org when scope=all-my-orgs; fall back to the
  // session's active org for any row the server didn't stamp (which
  // shouldn't happen in cross-org mode but is harmless if it does).
  const orgIndex = useMemo(() => {
    const m = new Map<string, { id: string; slug: string; name: string; docs: number; items: number }>();
    for (const d of docsQ.data?.data ?? []) {
      const o = d.org ?? null;
      if (!o) continue;
      const ex = m.get(o.id) ?? { id: o.id, slug: o.slug, name: o.name, docs: 0, items: 0 };
      ex.docs += 1;
      m.set(o.id, ex);
    }
    for (const w of itemsQ.data?.data ?? []) {
      const o = w.org ?? null;
      if (!o) continue;
      const ex = m.get(o.id) ?? { id: o.id, slug: o.slug, name: o.name, docs: 0, items: 0 };
      ex.items += 1;
      m.set(o.id, ex);
    }
    // Sort: active org first (so the user lands on a familiar list),
    // then alphabetical for predictability.
    const list = Array.from(m.values()).sort((a, b) => {
      if (a.id === activeOrgId) return -1;
      if (b.id === activeOrgId) return 1;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [docsQ.data, itemsQ.data, activeOrgId]);

  // If the URL pointed at an org that no longer appears in the data
  // (e.g. you unwatched the last item there), clear the filter so
  // we don't render an empty list with a stuck chip.
  useEffect(() => {
    if (orgFilter && !orgIndex.find((o) => o.id === orgFilter)) {
      setOrgFilter(null);
      writeOrgIdToUrl(null);
    }
  }, [orgFilter, orgIndex]);

  const filteredDocs = useMemo(() => {
    const all = docsQ.data?.data ?? [];
    if (!orgFilter) return all;
    return all.filter((d) => d.org?.id === orgFilter);
  }, [docsQ.data, orgFilter]);

  const filteredItems = useMemo(() => {
    const all = itemsQ.data?.data ?? [];
    if (!orgFilter) return all;
    return all.filter((w) => w.org?.id === orgFilter);
  }, [itemsQ.data, orgFilter]);

  const docCount = filteredDocs.length;
  const itemCount = filteredItems.length;
  const totalCount = docCount + itemCount;

  const handleOrgFilter = (id: string | null) => {
    setOrgFilter(id);
    writeOrgIdToUrl(id);
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Watching</h1>
          <div className="page-subtitle">
            {totalCount} item{totalCount === 1 ? '' : 's'} on your plate
            {' · '}
            {docCount} document{docCount === 1 ? '' : 's'}, {itemCount} work item
            {itemCount === 1 ? '' : 's'}
            {orgFilter && orgIndex.length > 1 && (
              <>
                {' · '}filtered to{' '}
                <span style={{ fontWeight: 600 }}>
                  {orgIndex.find((o) => o.id === orgFilter)?.name ?? 'org'}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {orgIndex.length >= 2 && (
        <OrgFilterStrip
          orgs={orgIndex}
          activeOrgId={orgFilter}
          onSelect={handleOrgFilter}
          totalDocs={docsQ.data?.data.length ?? 0}
          totalItems={itemsQ.data?.data.length ?? 0}
        />
      )}

      {/* CTSK-525: tabs left, view controls centred on the same row, right slot kept
          free for pagination. */}
      <div
        className="tabs"
        style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}
      >
        <div style={{ display: 'flex' }}>
          <button
            type="button"
            className={`tab${tab === 'documents' ? ' active' : ''}`}
            onClick={() => setTab('documents')}
          >
            Documents{docCount > 0 && <CountChip n={docCount} />}
          </button>
          <button
            type="button"
            className={`tab${tab === 'workitems' ? ' active' : ''}`}
            onClick={() => setTab('workitems')}
          >
            Work items{itemCount > 0 && <CountChip n={itemCount} />}
          </button>
        </div>
        <div style={{ justifySelf: 'center', paddingBottom: 2 }}>
          {tab === 'documents' && <CardViewBar v={cardView} />}
        </div>
        <div data-testid="watching-pager-slot" style={{ justifySelf: 'end', minWidth: 0 }} />
      </div>

      {tab === 'documents' && (
        <DocumentsTab
          rows={filteredDocs}
          loading={docsQ.isLoading}
          error={docsQ.isError}
          onNavigate={onNavigate}
          showOrgCol={orgIndex.length >= 2 && !orgFilter}
          view={cardView}
        />
      )}
      {tab === 'workitems' && (
        <WorkItemsTab
          rows={filteredItems}
          loading={itemsQ.isLoading}
          error={itemsQ.isError}
          onNavigate={onNavigate}
          showOrgCol={orgIndex.length >= 2 && !orgFilter}
          projects={projectsQ.data?.data ?? []}
        />
      )}
    </>
  );
}

function CountChip({ n }: { n: number }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 6,
        padding: '0 6px',
        minWidth: 18,
        height: 18,
        borderRadius: 999,
        background: 'var(--surface-sunken)',
        color: 'var(--text-secondary)',
        fontSize: 11,
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {n}
    </span>
  );
}

// ─── Org filter strip ────────────────────────────────────────────

function OrgFilterStrip({
  orgs,
  activeOrgId,
  onSelect,
  totalDocs,
  totalItems,
}: {
  orgs: { id: string; slug: string; name: string; docs: number; items: number }[];
  activeOrgId: string | null;
  onSelect: (id: string | null) => void;
  totalDocs: number;
  totalItems: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginBottom: 12,
        padding: '8px 0',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <span
        style={{
          alignSelf: 'center',
          marginRight: 4,
          fontSize: 11,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        Org
      </span>
      <OrgChip
        label="All orgs"
        count={totalDocs + totalItems}
        active={activeOrgId === null}
        onClick={() => onSelect(null)}
      />
      {orgs.map((o) => (
        <OrgChip
          key={o.id}
          label={o.name}
          count={o.docs + o.items}
          active={activeOrgId === o.id}
          onClick={() => onSelect(o.id)}
          title={`${o.slug} · ${o.docs} doc${o.docs === 1 ? '' : 's'}, ${o.items} item${o.items === 1 ? '' : 's'}`}
        />
      ))}
    </div>
  );
}

function OrgChip({
  label,
  count,
  active,
  onClick,
  title,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title ?? label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        height: 26,
        borderRadius: 999,
        border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
        background: active ? 'color-mix(in srgb, var(--accent-primary) 12%, transparent)' : 'var(--surface-base)',
        color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: active ? 600 : 500,
      }}
    >
      <span>{label}</span>
      <span
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: 11,
          color: active ? 'var(--accent-primary)' : 'var(--text-muted)',
        }}
      >
        {count}
      </span>
    </button>
  );
}

function OrgCell({ org }: { org?: { id: string; slug: string; name: string } }) {
  if (!org) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  return (
    <span
      title={org.name}
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 999,
        background: 'var(--surface-sunken)',
        color: 'var(--text-secondary)',
        fontSize: 11,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 120,
      }}
    >
      {org.slug}
    </span>
  );
}

// ─── Documents tab ───────────────────────────────────────────────

function DocumentsTab({
  rows,
  loading,
  error,
  onNavigate,
  showOrgCol,
  view,
}: {
  rows: DocumentListItem[];
  loading: boolean;
  error: boolean;
  onNavigate: (p: string) => void;
  showOrgCol: boolean;
  view: CardViewState;
}) {
  const qc = useQueryClient();
  const cardActions = useDocCardActions(onNavigate);
  const unwatch = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/documents/${id}/watch`, { method: 'DELETE' }),
    onSuccess: () => {
      // Both the watching list AND any document list query (which
      // also reads isWatchedByMe per-row) need to refresh.
      qc.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Unwatched');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (loading) return <SkeletonRows rows={6} />;
  if (error) return <p style={{ color: 'var(--danger-primary)' }}>Couldn't load watched documents.</p>;
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No watched documents."
        body="Star a document from its detail page or the row menu to subscribe."
      />
    );
  }

  if (view.mode !== 'list') {
    return (
      <DocumentCardWall
        docs={rows}
        mode={view.mode as 'masonry' | 'grid'}
        expanded={view.expanded}
        columns={view.cols}
        previewAll={view.previewAll}
        scaled={view.scaled}
        actions={cardActions}
        highlightId={view.highlightId}
        highlightEffect={view.highlightEffect}
        onToggle={view.toggleCard}
        onOpen={(id) => { rememberOpened(id); onNavigate(`/documents/${id}`); }}
        renderExtras={(d) => (
          <>
            <StatusBadge status={d.status} variant="document" title={`${d.title}: ${d.status}`} />
            {showOrgCol && d.org && <OrgCell org={d.org} />}
          </>
        )}
      />
    );
  }

  return (
    <table className="grid">
      <thead>
        <tr>
          {/* CTSK-237 Part C — star column moved leftmost to match the
              WorkItemsList star column family (was rightmost). */}
          <th style={{ width: 32 }}></th>
          <th style={{ width: 28 }}></th>
          <th>Title</th>
          {showOrgCol && <th style={{ width: 130 }}>Org</th>}
          <th style={{ width: 110 }}>Status</th>
          <th style={{ width: 130 }}>Updated</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d) => (
          <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => onNavigate(`/documents/${d.id}`)}>
            {/* CTSK-237 Part C — restyled to the WorkItemsList star
                button pattern (accent color, fontSize 16) per the
                2026-07-27 design-consistency call. Always filled ★ on
                this surface: every row here is watched by definition,
                so click = unwatch (existing mutation, semantics
                unchanged) — the empty-☆ toggle-add path lives on the
                source lists. */}
            <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
              <button
                type="button"
                aria-label="Unwatch"
                title="Unwatch"
                aria-pressed={true}
                onClick={(e) => {
                  e.stopPropagation();
                  unwatch.mutate(d.id);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 2,
                  fontSize: 16,
                  color: 'var(--accent-primary)',
                  lineHeight: 1,
                }}
              >
                ★
              </button>
            </td>
            <td
              title={iconLabelForMime(d.mimeType)}
              style={{ color: 'var(--text-muted)', textAlign: 'center' }}
            >
              <MimeIcon mime={d.mimeType} size={16} />
            </td>
            <td
              title={d.title}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 360 }}
            >
              {d.title}
            </td>
            {showOrgCol && (
              <td>
                <OrgCell org={d.org} />
              </td>
            )}
            <td>
              <StatusBadge status={d.status} variant="document" title={`${d.title}: ${d.status}`} />
            </td>
            <td style={{ whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: 12 }}>
              {new Date(d.updatedAt).toLocaleDateString()}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Work items tab ─────────────────────────────────────────────

function WorkItemsTab({
  rows,
  loading,
  error,
  onNavigate,
  showOrgCol,
  projects,
}: {
  rows: WorkItem[];
  loading: boolean;
  error: boolean;
  onNavigate: (p: string) => void;
  showOrgCol: boolean;
  /** Active-org projects for per-key color lookup (0.1.51). Cross-org
   *  rows whose project isn't in this list fall back to --text-muted. */
  projects: Project[];
}) {
  const qc = useQueryClient();
  const unwatch = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/workitems/${id}/watch`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workitems'] });
      toast.success('Unwatched');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Past-due first, then needs-attention, then everything else — the
  // whole point of the watching page is to surface what needs your
  // hand. Within each bucket, newest updates win.
  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aFlag = a.isPastDue ? 0 : a.needsAttention ? 1 : 2;
      const bFlag = b.isPastDue ? 0 : b.needsAttention ? 1 : 2;
      if (aFlag !== bFlag) return aFlag - bFlag;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [rows]);

  if (loading) return <SkeletonRows rows={6} />;
  if (error) return <p style={{ color: 'var(--danger-primary)' }}>Couldn't load watched work items.</p>;
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No watched work items."
        body="Star a work item from its detail page or the row menu to subscribe."
      />
    );
  }

  return (
    <table className="grid">
      <thead>
        <tr>
          {/* CTSK-237 Part C — star column moved leftmost to match the
              WorkItemsList star column family (was rightmost). */}
          <th style={{ width: 32 }}></th>
          <th style={{ width: 80 }}>Key</th>
          <th>Title</th>
          {showOrgCol && <th style={{ width: 130 }}>Org</th>}
          <th style={{ width: 110 }}>Status</th>
          <th style={{ width: 90 }}>Priority</th>
          <th style={{ width: 90 }}>Due</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((w) => {
          const tint = w.isPastDue
            ? 'color-mix(in srgb, var(--danger-primary) 6%, transparent)'
            : w.needsAttention
            ? 'color-mix(in srgb, #A56B00 6%, transparent)'
            : undefined;
          return (
            <tr
              key={w.id}
              style={{ cursor: 'pointer', background: tint }}
              onClick={() => onNavigate(`/workitems/${w.id}`)}
            >
              {/* CTSK-237 Part C — restyled to the WorkItemsList star
                  button pattern (accent color, fontSize 16) per the
                  2026-07-27 design-consistency call. Always filled ★
                  here — every row is watched by definition, so click =
                  unwatch (existing mutation, semantics unchanged). */}
              <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  aria-label="Unwatch"
                  title="Unwatch"
                  aria-pressed={true}
                  onClick={(e) => {
                    e.stopPropagation();
                    unwatch.mutate(w.id);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 2,
                    fontSize: 16,
                    color: 'var(--accent-primary)',
                    lineHeight: 1,
                  }}
                >
                  ★
                </button>
              </td>
              <td
                // 0.1.51 — key color from project.color (matches other
                // work item key renderers). Cross-org rows whose project
                // isn't in the active-org list fall back to --text-muted.
                style={(() => {
                  const projectColor = projects.find((p) => p.id === w.projectId)?.color;
                  return {
                    whiteSpace: 'nowrap',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    color: projectColor ?? 'var(--text-muted)',
                    fontWeight: projectColor ? 600 : undefined,
                  };
                })()}
                title={formatWorkItemKey(w).secondary ?? undefined}
              >
                {formatWorkItemKey(w).primary}
              </td>
              <td
                title={w.title}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}
              >
                {w.title}
              </td>
              {showOrgCol && (
                <td>
                  <OrgCell org={w.org} />
                </td>
              )}
              <td>
                <StatusBadge status={w.status} variant="document" title={`${formatWorkItemKey(w).primary}: ${w.status}`} />
              </td>
              <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{w.priority}</td>
              <td
                style={{
                  whiteSpace: 'nowrap',
                  color: w.isPastDue ? 'var(--danger-primary)' : 'var(--text-muted)',
                  fontWeight: w.isPastDue ? 600 : undefined,
                  fontSize: 12,
                }}
              >
                {w.dueDate ? new Date(w.dueDate).toLocaleDateString() : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
